import type { SetupKitsDetail } from "@/types/onboarding";

// Brand-kit id contract shared with branddetails-v2 (internal/setup/kits.go):
// lowercase ASCII letters/digits/hyphens, must start alphanumeric, 1–40 chars.
// "default" is shape-valid but reserved — it is the implicit legacy kit.
export const DEFAULT_KIT_ID = "default";
export const KIT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

export function isValidKitId(id: string): boolean {
  return KIT_ID_PATTERN.test(id);
}

// Router search object for kit-preserving wizard navigation. Always names the
// kit — including the default one — because inside the wizard an absent `kit`
// means "open whatever is applied to the site" (see entryKit and the route's
// beforeLoad), which would bounce a deliberate default-kit edit to the live
// treatment. Callers spread this into a navigate/redirect `search`.
export function kitSearch(kit: string): { kit: string } {
  return { kit };
}

// Kit a URL that enters the wizard without a `?kit=` should open: the one
// that is live on the site. Falls back to the default kit when there is no
// registry (backend without kit support), nothing has been applied yet, the
// applied kit has since been deleted (redirecting into it would 404), or its
// id is shape-invalid. That last guard is not paranoia: the route redirects to
// `?kit=<this>` and the search schema strips anything the pattern rejects, so
// returning an unrepresentable id would bounce beforeLoad between "no kit" and
// "redirect" forever.
export function entryKit(detail: SetupKitsDetail | null | undefined): string {
  const applied = detail?.appliedKit;
  return applied &&
    isValidKitId(applied) &&
    detail?.kits.some((k) => k.id === applied)
    ? applied
    : DEFAULT_KIT_ID;
}

// Derives a kit id from a display name. May return "" (nothing sluggable) —
// callers treat that as invalid.
export function slugifyKitId(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}
